import { Component, type ReactNode, type ErrorInfo } from 'react';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors in the tree below it and shows a fallback
 * instead of unmounting to a blank page. Used at the App and ResultScreen
 * levels so a single broken section doesn't take down the whole app.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div role="alert" className={styles.wrap}>
        <div className={styles.card}>
          <span className={styles.eyebrow}>Render Error</span>
          <h1 className={styles.title}>Something tripped while drawing this page.</h1>
          <p className={styles.body}>
            The analysis result loaded, but a section of the result UI errored on it. Most often this means the
            backend returned a field shape one of the components doesn't yet handle. Below is the underlying error —
            useful for the diff.
          </p>
          <pre className={styles.pre}>{error.message}</pre>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} onClick={() => location.assign('/')}>
              Back to input
            </button>
            <button type="button" className={styles.secondary} onClick={this.reset}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
